<script lang="ts">
  /**
   * NEXUS-MU root.
   *
   * Layout: optional brand topbar (only shown standalone) + Toolbar
   * (always) + Constellation (the deck.gl viewport) + MemoryDetail
   * (right slide-in when a point is selected).
   *
   * Two modes by construction:
   *   - hosted: iframed inside DC; brand topbar hidden; addon protocol active
   *   - standalone: opened directly at mu.psyopsys.ai; brand visible
   */

  import { onMount } from 'svelte'
  import Constellation from './lib/Constellation.svelte'
  import StatusBar from './lib/StatusBar.svelte'
  import Drawer from './lib/Drawer.svelte'
  import MemoryDetail from './lib/MemoryDetail.svelte'
  import AddMemoryDialog from './lib/AddMemoryDialog.svelte'
  import IngestDocumentDialog from './lib/IngestDocumentDialog.svelte'
  import McoAuthGate from './lib/McoAuthGate.svelte'
  import TaskGraph from './lib/TaskGraph.svelte'
  import { proto, startProtocol } from './lib/addonProtocol.svelte'
  import { layoutPoints } from './lib/layoutStore.svelte'
  import { drawer } from './lib/selection.svelte'
  import { appView } from './lib/taskGraphStore.svelte'

  onMount(() => {
    return startProtocol()
  })

  /* Trail is NO LONGER auto-built from every selection change. Trail
   * steps now come from intentional sequence-walk operations only:
   *   - Walk camera auto-advance (Constellation.svelte's walkNext)
   *   - Follow Strongest button (MemoryDetail.svelte)
   *   - Clicking a relationship row in the panel (MemoryDetail.svelte)
   * Manual point clicks navigate freely without polluting the trail. */
</script>

<main class="mu-root">
  {#if !proto.isHosted}
    <header class="mu-topbar">
      <button
        class="mu-brand-mark"
        type="button"
        aria-label="Open controls drawer"
        aria-expanded={drawer.open}
        onclick={() => drawer.open = !drawer.open}
      >
        <span class="mu-brand-mark-text">MU</span>
        <span class="mu-brand-mark-hint" aria-hidden="true">≡</span>
      </button>
      <span class="mu-version">NEXUS-MU v0.1</span>
      <span class="mu-mode">// {appView.mode}</span>
      <span class="mu-tagline">memory · projected · server-side</span>
    </header>
  {/if}

  <StatusBar />

  <div class="mu-stage">
    {#if appView.mode === 'tasks'}
      <TaskGraph />
    {:else}
      <Constellation />
      <MemoryDetail />
      <Drawer points={layoutPoints.all} />
    {/if}
  </div>

  <AddMemoryDialog />
  <IngestDocumentDialog />

  {#if !proto.isHosted}
    <McoAuthGate />
  {/if}
</main>

<style>
  .mu-root {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--surface);
  }

  .mu-topbar {
    display: flex;
    align-items: center;
    gap: var(--s-3);
    padding: var(--s-2) var(--s-3);
    border-bottom: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    flex-shrink: 0;
  }
  /* MU mark — square brand button mirroring DC's brand-mark pattern.
   * Doubles as a drawer trigger (alongside the StatusBar hamburger). */
  .mu-brand-mark {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border: 1px solid var(--accent);
    background: var(--surface);
    color: var(--accent);
    display: grid;
    place-items: center;
    cursor: pointer;
    position: relative;
    transition: box-shadow var(--t-fast);
    padding: 0;
    font-family: inherit;
  }
  .mu-brand-mark:hover,
  .mu-brand-mark[aria-expanded='true'] {
    box-shadow: 0 0 6px var(--accent-soft);
  }
  .mu-brand-mark-text {
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.05em;
    transition: opacity var(--t-fast);
  }
  .mu-brand-mark-hint {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    color: var(--accent);
    font-size: 15px;
    line-height: 1;
    opacity: 0;
    transition: opacity var(--t-fast);
    pointer-events: none;
  }
  .mu-brand-mark:hover .mu-brand-mark-text,
  .mu-brand-mark[aria-expanded='true'] .mu-brand-mark-text { opacity: 0; }
  .mu-brand-mark:hover .mu-brand-mark-hint,
  .mu-brand-mark[aria-expanded='true'] .mu-brand-mark-hint { opacity: 1; }
  .mu-version {
    color: var(--on-surface-faint);
    font-size: 10px;
  }
  .mu-mode {
    color: var(--on-surface-variant);
  }
  .mu-tagline {
    color: var(--on-surface-faint);
    font-size: 10px;
    margin-left: auto;
  }

  .mu-stage {
    flex: 1;
    min-height: 0;
    position: relative;
  }
</style>
